import React, { createContext, useReducer, useContext, useCallback, useRef, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../../utils/apiConfig';

const MaterialReservationContext = createContext();

const cachedReservations = getInitialReservations();
const initialState = {
    reservations: cachedReservations,
    loading: cachedReservations.length === 0,
    error: null,
    selectedDraaidag: null,
    currentReservation: null,
    lastFetch: null,
    cache: {}
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minuten cache
const RESERVATIONS_LS_KEY = 'reservationsCache';

// Read reservations from localStorage for instant display
function getInitialReservations() {
    try {
        const cached = localStorage.getItem(RESERVATIONS_LS_KEY);
        if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed?.data && Array.isArray(parsed.data) && (Date.now() - parsed.timestamp) < 30 * 60 * 1000) {
                return parsed.data; // Use if less than 30 min old
            }
        }
    } catch (e) { /* ignore */ }
    return [];
}

const reducer = (state, action) => {
    switch (action.type) {
        case 'FETCH_START':
            return { ...state, loading: true, error: null };
        case 'FETCH_SUCCESS':
            return { 
                ...state, 
                loading: false, 
                reservations: action.payload,
                lastFetch: Date.now(),
                cache: {
                    ...state.cache,
                    [action.cacheKey || 'default']: {
                        data: action.payload,
                        timestamp: Date.now()
                    }
                }
            };
        case 'FETCH_ERROR':
            return { ...state, loading: false, error: action.payload };
        case 'SET_SELECTED_DRAAIDAG':
            return { ...state, selectedDraaidag: action.payload, currentReservation: null }; // Clear currentReservation when draaidag changes
        case 'SET_CURRENT_RESERVATION':
            return { ...state, currentReservation: action.payload };
        case 'CLEAR_CACHE':
            return { ...state, cache: {} };
        case 'UPDATE_RESERVATION_FIELD': {
            const { reservationId, fieldName, value } = action.payload;
            let updatedCurrentReservation = state.currentReservation;

            const updatedReservations = state.reservations.map(res => {
                if (res.id === reservationId) {
                    const updatedReservation = {
                        ...res,
                        fields: {
                            ...res.fields,
                            [fieldName]: value
                        }
                    };
                    // Also update the currentReservation if it's the one being changed
                    if (state.currentReservation?.id === reservationId) {
                        updatedCurrentReservation = updatedReservation;
                    }
                    return updatedReservation;
                }
                return res;
            });

            const cacheKey = 'all_reservations';
            const cachedData = state.cache[cacheKey] || {};

            const updatedCache = {
                ...state.cache,
                [cacheKey]: {
                    ...cachedData,
                    data: updatedReservations,
                    timestamp: Date.now()
                }
            };
            
            return { ...state, reservations: updatedReservations, cache: updatedCache, currentReservation: updatedCurrentReservation };
        }
        default:
            return state;
    }
};

export const MaterialReservationProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const fetchPromiseRef = useRef(null);

    const fetchReservations = useCallback(async (forceRefresh = false) => {
        // Check cache eerst
        const cacheKey = 'all_reservations';
        const cached = state.cache[cacheKey];
        
        if (!forceRefresh && cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
            return cached.data;
        }

        // Prevent duplicate fetches
        if (fetchPromiseRef.current) {
            return fetchPromiseRef.current;
        }

        // Alleen de globale loading-state aanzetten bij een eerste fetch (geen cache),
        // anders flikkert de overlay bij elke polling-refresh.
        if (!cached) dispatch({ type: 'FETCH_START' });
        
        const fetchPromise = (async () => {
            try {
                // Met cache is dit de polling-verversing: niet meetellen in de laadbalk (laadStatus).
                const response = await axios.get(API_ENDPOINTS.reservations.list(), cached ? { stil: true } : undefined);
                const allReservations = response.data || [];
                
                
                dispatch({ type: 'FETCH_SUCCESS', payload: allReservations, cacheKey });
                // Persist to localStorage for instant display on next page load
                try { localStorage.setItem(RESERVATIONS_LS_KEY, JSON.stringify({ data: allReservations, timestamp: Date.now() })); } catch (e) { /* ignore */ }
                return allReservations;
            } catch (error) {
                console.error('Error fetching reservations with draaidag info:', error);
                dispatch({ type: 'FETCH_ERROR', payload: error.message });
                throw error;
            } finally {
                fetchPromiseRef.current = null;
            }
        })();

        fetchPromiseRef.current = fetchPromise;
        return fetchPromise;
    }, [state.cache]);

    // Automatically fetch reservations when the provider mounts
    useEffect(() => {
        fetchReservations();
    }, [fetchReservations]);

    // Focus + polling refresh: pak wijzigingen van collega's op zonder dat de gebruiker
    // hoeft te switchen. Pollt elke 20s zolang de tab visible is.
    const fetchReservationsRef = useRef(fetchReservations);
    fetchReservationsRef.current = fetchReservations;
    useEffect(() => {
        const refresh = () => fetchReservationsRef.current?.(true);
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') refresh();
        };
        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('focus', handleVisibility);
        const intervalId = setInterval(() => {
            if (document.visibilityState === 'visible') refresh();
        }, 20000);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('focus', handleVisibility);
            clearInterval(intervalId);
        };
    }, []);

    const clearCache = useCallback(() => {
        dispatch({ type: 'CLEAR_CACHE' });
    }, []);

    const [searchParams, setSearchParams] = useSearchParams();
    const initialDraaidagId = searchParams.get('draaidagId');

    const setSelectedDraaidag = useCallback((draaidag) => {
        dispatch({ type: 'SET_SELECTED_DRAAIDAG', payload: draaidag });
        // Update URL when draaidag is selected (without page reload)
        if (draaidag?.id) {
            setSearchParams({ draaidagId: draaidag.id }, { replace: true });
        } else {
            // Remove param when deselecting
            setSearchParams({}, { replace: true });
        }
    }, [setSearchParams]);

    const setCurrentReservation = useCallback((reservation) => {
        dispatch({ type: 'SET_CURRENT_RESERVATION', payload: reservation });
    }, []);

    const updateReservationField = useCallback((reservationId, fieldName, value) => {
        dispatch({ type: 'UPDATE_RESERVATION_FIELD', payload: { reservationId, fieldName, value } });
    }, []);

    // Memoize de context value om onnodige re-renders te voorkomen
    const value = useMemo(() => ({
        ...state,
        fetchReservations,
        setSelectedDraaidag,
        setCurrentReservation,
        clearCache,
        updateReservationField,
        initialDraaidagId
    }), [state, fetchReservations, setSelectedDraaidag, setCurrentReservation, clearCache, updateReservationField, initialDraaidagId]);

    return (
        <MaterialReservationContext.Provider value={value}>
            {children}
        </MaterialReservationContext.Provider>
    );
};

export const useMaterialReservation = () => {
    const context = useContext(MaterialReservationContext);
    if (context === undefined) {
        throw new Error('useMaterialReservation must be used within a MaterialReservationProvider');
    }
    return context;
};
